// src/services/analytics.ts
import { Capacitor } from '@capacitor/core';
import { app } from '../lib/firebase';

export type RoddoAnalyticsEvent =
  | 'app_open' | 'auth_ready' | 'legal_gate_shown' | 'legal_accepted'
  | 'welcome_shown' | 'tour_started' | 'tour_skipped' | 'tour_completed'
  | 'big_blue_opened' | 'fishiq_opened'
  | 'search_lure_search_started' | 'search_lure_search_succeeded' | 'search_lure_search_failed'
  | 'fishiq_species_opened' | 'fishiq_log_this_fish' | 'log_catch_opened'
  | 'catch_save_started' | 'catch_save_succeeded' | 'catch_save_failed'
  | 'community_share_succeeded' | 'community_share_failed'
  | 'tournament_submission_succeeded' | 'tournament_submission_failed'
  | 'rewarded_ad_load_started' | 'rewarded_ad_loaded' | 'rewarded_ad_showed'
  | 'rewarded_ad_completed' | 'rewarded_ad_failed';

type SafeValue = string | number | boolean;
type SafeParams = Record<string, SafeValue | null | undefined>;

const ALLOWED = new Set([
  'source','account_type','platform','app_version','app_build','replay','skipped',
  'sync_status','result_count','reason_code','reused_area','share_requested',
  'tournament_mode','edit_mode','photo_count','outcome',
  'placement','ad_format','reward_amount',
]);

let analyticsPromise: Promise<any | null> | null = null;
const clean = (v: unknown, max=80) => String(v ?? '').trim().replace(/\s+/g,'_').slice(0,max);
const platform = (() => { try { return clean(Capacitor.getPlatform?.() || 'web', 24) || 'web'; } catch { return 'web'; } })();
const appVersion = clean((import.meta as any)?.env?.VITE_APP_VERSION || '', 32);
const appBuild = clean((import.meta as any)?.env?.VITE_APP_BUILD || (import.meta as any)?.env?.VITE_BUILD_NUMBER || '', 32);

function sanitize(params: SafeParams = {}) {
  const out: Record<string,string|number> = { platform };
  if (appVersion) out.app_version = appVersion;
  if (appBuild) out.app_build = appBuild;
  for (const [key, raw] of Object.entries(params)) {
    if (!ALLOWED.has(key) || raw == null) continue;
    if (typeof raw === 'boolean') out[key] = raw ? 1 : 0;
    else if (typeof raw === 'number') { if (Number.isFinite(raw)) out[key] = raw; }
    else { const value = clean(raw); if (value) out[key] = value; }
  }
  return out;
}

async function getRoddoAnalytics() {
  if (analyticsPromise) return analyticsPromise;
  analyticsPromise = (async () => {
    try {
      if (typeof window === 'undefined') return null;
      const mod = await import('firebase/analytics');
      if (!(await mod.isSupported().catch(() => false))) return null;
      return mod.getAnalytics(app);
    } catch (error) {
      console.debug('[analytics] unavailable; continuing without telemetry', error);
      return null;
    }
  })();
  return analyticsPromise;
}

export function trackRoddoEvent(eventName: RoddoAnalyticsEvent, params: SafeParams = {}): void {
  const safe = sanitize(params);
  if ((import.meta as any)?.env?.DEV) {
    console.debug('[analytics]', eventName, safe);
    return;
  }
  void getRoddoAnalytics().then(async (analytics) => {
    if (!analytics) return;
    try {
      const mod = await import('firebase/analytics');
      mod.logEvent(analytics, eventName, safe);
    } catch (error) {
      console.debug('[analytics] event skipped', eventName, error);
    }
  });
}
